"use client";

import Image from "next/image";
import { Suspense } from "react";

import dynamic from "next/dynamic";
import Link from "next/link";

import { Button, ButtonVariantTypes, getButtonVariantStylesAndSize } from "components/ui/button";
import { ImageSliderProps } from "types/tiles";

import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import { useDialog } from "components/Tiles/context/DialogClientProvider";

const MuxPlayer = dynamic(() => import("components/Tiles/commonComponents/MuxPlayer"));
const ImageSlider = dynamic(() => import("./ImageSlider"), { ssr: false });

interface TitleTextMediaProps {
  title: string;
  subtitle: string;
  content: string;
  buttonText: string;
  buttonUrl: string;
  buttonVariant?: ButtonVariantTypes;
  showButton: boolean;
  mediaType: "image" | "video" | "slider";
  videoId?: string;
  src: string;
  miniTitle: string;
  bgColor: string;
  thumbnailTime: number;
  images?: ImageSliderProps[];
  titleTag: React.ElementType;
  miniTitleTag: React.ElementType;
  autoPlay?: boolean;
  hasPriority?: boolean;
  useGifThumbnail?: boolean;
}

const TitleTextMedia: React.FC<TitleTextMediaProps> = ({
  title,
  miniTitle,
  subtitle,
  mediaType,
  videoId,
  thumbnailTime,
  bgColor,
  src,
  buttonVariant = ButtonVariantTypes.DEFAULT,
  images,
  buttonText,
  buttonUrl,
  showButton,
  content,
  titleTag = "p",
  miniTitleTag = "p",
  autoPlay = false,
  hasPriority = false,
  useGifThumbnail = false
}) => {
  const TitleTag = titleTag;
  const MiniTitleTag = miniTitleTag;
  const { openDialog } = useDialog();

  return (
    <section id="TitleTextMedia-44" style={{ backgroundColor: bgColor }}>
      <MaxWidthWrapper className="tile-container-sm">
        <div className="grid grid-cols-1 content-center gap-4 pt-4 lg:grid-cols-12 xl:gap-24">
          <div className="relative z-10 order-2 col-span-1 self-center py-4 lg:order-1 lg:col-span-6">
            <MiniTitleTag className="subby mx-auto !text-center  lg:mx-0 lg:!text-left">{miniTitle}</MiniTitleTag>
            <TitleTag className="tile-main-header text-center font-heading [text-wrap:balance] lg:text-left">
              {title}
            </TitleTag>
            <p className="pb-2 text-center text-base [text-wrap:balance] lg:!pb-6 lg:!text-left">{subtitle}</p>
            {content && content.length > 0 && (
              <p className="font-matter text-base font-normal [text-wrap:balance]">{content}</p>
            )}

            {showButton && buttonUrl && buttonText && buttonVariant !== ButtonVariantTypes.DIALOG ? (
              <div className="mx-auto max-w-[350px] lg:mx-0">
                <Link
                  className={getButtonVariantStylesAndSize({
                    variant: ButtonVariantTypes.BRAND2,
                    size: "better"
                  })}
                  href={buttonUrl}
                  title={buttonText}
                  prefetch={false}
                >
                  {buttonText}
                </Link>
              </div>
            ) : buttonVariant === ButtonVariantTypes.DIALOG ? (
              // We can use the buttonVariant prop to display the open dialog button conditionally
              <Button title={buttonText} onClick={openDialog} variant={ButtonVariantTypes.BRAND} size="lg">
                {buttonText}
              </Button>
            ) : null}
          </div>

          <div className="relative order-1 col-span-1 h-full content-center lg:col-span-6">
            <div className="aspect-square h-[250px] w-full max-w-full lg:h-[500px] lg:min-h-[490px] ">
              {mediaType === "image" && (
                <Image
                  alt="icon"
                  fill
                  className="border-primary-[#E8ECED]  z-10 h-full w-full overflow-hidden rounded-xl border object-cover"
                  src={src}
                  sizes="(max-width: 942px) 100vw, 50vw"
                />
              )}
              {mediaType === "video" && (
                <Suspense>
                  <MuxPlayer
                    playbackId={videoId}
                    thumbnailTime={thumbnailTime}
                    style={
                      {
                        "--center-controls": "block"
                      } as React.CSSProperties
                    }
                    muted={false}
                    className="border-primary-[#E8ECED] z-10 h-full w-full overflow-hidden rounded-3xl object-cover"
                    autoPlay={autoPlay}
                    hasPriority={hasPriority}
                    useGifThumbnail={useGifThumbnail}
                    gestureControl
                  />
                </Suspense>
              )}
              {mediaType === "slider" && images && (
                <Suspense>
                  <ImageSlider images={images} />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default TitleTextMedia;
